import { AppIcons } from 'app/shared';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { HeaderCountsModel } from 'app/core/models';
import { UserPermission } from 'app/core/constants';
import { DomSanitizer } from '@angular/platform-browser';
import { COMMON_SERVICE, CommonInterface } from 'app/core/services/common.interface';
import { KmrAccessModel, KmrAccessService, StoreStateService, USER_AUTHENTICATION_SERVICE, UserAuthInterface } from 'app/core/services';

@Injectable({
	providedIn: 'root'
})

export class AppMenuService implements OnDestroy {

	//#region Feilds
	headerCounts = new HeaderCountsModel();
	private appMenusSubject = new BehaviorSubject<any[]>([]);
	private inwardCountSubscription?: Subscription;
	private storeStateSubscription?: Subscription;
	private kmrAccessSubscription?: Subscription;
	private kmrAccess: KmrAccessModel = { mapped: false, isFranchise: false, isAdmin: false };
	appMenus$ = this.appMenusSubject.asObservable();
	// #endregion

	//#region Constructor
	constructor(private sanitizer: DomSanitizer,
		@Inject(COMMON_SERVICE) private _commonService: CommonInterface,
		private _storeState: StoreStateService,
		@Inject(USER_AUTHENTICATION_SERVICE) private _userAuthInterface: UserAuthInterface,
		private _kmrAccessService: KmrAccessService
	) {
		this.refreshMenus();
		this.subscribeToStoreChanges();
		this.subscribeToKmrAccessChanges();
	}
	//#endregion

	loadInwardCountDetails() {
		if (!this._userAuthInterface?.authenticatedUser?.token) {
			return;
		}

		this.inwardCountSubscription?.unsubscribe();
		this.inwardCountSubscription = this._commonService.getInwardCountData().subscribe({
			next: (response) => {
				this.headerCounts.goldInwardCount = response.data.goldInwardCount;
				this.headerCounts.diamondInwardCount = response.data.diamondInwardCount;
				this.headerCounts.goldSnmccInwardCount = response.data.goldSnmccInwardCount;
				this.headerCounts.diamondSnmccInwardCount = response.data.diamondSnmccInwardCount;
				this.headerCounts.interStoreInwardCount = response.data.interStoreInwardCount;
				this.headerCounts.repairInwardCount = response.data.repairInwardCount;
				this.headerCounts.diamondConsignmentInwardCount = response.data.diamondConsignmentInwardCount;
				this.headerCounts.goldConsignmentInwardCount = response.data.goldConsignmentInwardCount;
				this._commonService.setNotificationCount(response.data.notificationCount ?? 0);

				this.refreshMenus();
			},
			error: (err) => {
				console.error('Error fetching inward count data:', err);
			}
		});
	}

	private subscribeToStoreChanges(): void {
		this.storeStateSubscription = this._storeState.storeId$.subscribe(id => {
			if (id != null && this._userAuthInterface?.authenticatedUser) {
				this.loadInwardCountDetails();
				this._kmrAccessService.refresh();
			} else if (id == null) {
				this.resetCounts();
				this._kmrAccessService.reset();
			}
		});
	}

	private subscribeToKmrAccessChanges(): void {
		this.kmrAccessSubscription = this._kmrAccessService.access$.subscribe(access => {
			this.kmrAccess = access;
			this.refreshMenus();
		});
	}

	public resetCounts(): void {
		this.inwardCountSubscription?.unsubscribe();
		this.headerCounts = new HeaderCountsModel();
		this._commonService.setNotificationCount(0);
		this.refreshMenus();
	}

	ngOnDestroy(): void {
		this.inwardCountSubscription?.unsubscribe();
		this.storeStateSubscription?.unsubscribe();
		this.kmrAccessSubscription?.unsubscribe();
	}

	private refreshMenus() {
		this.appMenusSubject.next(this.getAppMenus());
	}

	getAppMenus() {
		const menus = [
			// {
			// 	'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.dashboard),
			// 	'title': 'Dashboard',
			// 	'url': '/dashboard',
			// 	'systemNameList': [
			// 		UserPermission.Dashboard.Read,
			// 	],
			// },
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.dashboard),
				'title': 'Dashboard',
				'url': '/kisna-live-dashboard',
				'systemNameList': [
					UserPermission.KisnaLiveDashboard.Read,
				],
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.master),
				'title': 'Master',
				'caret': true,
				'systemNameList': [
					UserPermission.Customer.Read, UserPermission.Customer.Write, UserPermission.Customer.Delete,
					UserPermission.RepairWork.Read,
					UserPermission.OldGoldPurchaseRate.Read,
					UserPermission.ReturnDeduction.Read,
					UserPermission.GoldMakingCharges.Read,
					UserPermission.ConsumerOffers.Read,
					UserPermission.DiamondMakingCharges.Read,
					UserPermission.FAQMaster.Read, UserPermission.FAQMaster.Write, UserPermission.FAQMaster.Delete,
					UserPermission.StoreExpence.Read, UserPermission.StoreExpence.Write,
					UserPermission.WalkInSource.Read,
					UserPermission.SalesPerson.Read, UserPermission.SalesPerson.Write,
					UserPermission.CashEntry.Read, UserPermission.CashEntry.Write, UserPermission.CashEntry.Delete,
					UserPermission.User.Read, UserPermission.User.Write, UserPermission.User.Delete,
					UserPermission.Roles.Read, UserPermission.Roles.Write, UserPermission.Roles.Delete,
					UserPermission.PromoMaster.Read,
					UserPermission.CashEntry.Write,
					UserPermission.NotificationCenter.Write,
					UserPermission.GDMakingCharge.Read,
				],

				'submenu': [
					{
						'title': 'Store',
						'requiresStoreSelection': true,
						'caret': true,
						'systemNameList': [
							UserPermission.Store.Read,
							UserPermission.BillSetting.Read,
							UserPermission.BillType.Read,
						],
						'submenu': [
							{
								'url': '/store-master',
								'title': 'Store List',
								'requiresStoreSelection': true,
								'childSystemNameList': [UserPermission.Store.Read]
							},
							{
								'url': '/bill-serial-number',
								'title': 'Bill Serial Number',
								'requiresStoreSelection': true,
								'childSystemNameList': [UserPermission.BillSetting.Read]
							}
						]
					}, {
						'url': '/customer-master/list',
						'title': 'Customer Master',
						'childSystemNameList': [
							UserPermission.Customer.Read,
							UserPermission.Customer.Write,
							UserPermission.Customer.Delete,
						]
					},
					{
						'url': '/sales-person',
						'title': 'Sales Person',
						'childSystemNameList': [
							UserPermission.SalesPerson.Read,
							UserPermission.SalesPerson.Write
						]
					},
					{
						'url': '/gold-rate-list',
						'title': 'Gold Rate',
						'childSystemNameList': [
							UserPermission.GoldRate.Read
						]
					},
					{
						'url': '/diamond-making-Charges-list',
						'title': 'Diamond Making Charges',
						'childSystemNameList': [UserPermission.DiamondMakingCharges.Read]
					},
					{
						'url': '/gold-making-charges',
						'title': 'Gold Making Charges',
						'childSystemNameList': [UserPermission.GoldMakingCharges.Read]
					},

					{
						'url': '/gold-rate/list',
						'title': 'Kisna Old Gold Purchase Rate',
						'childSystemNameList': [UserPermission.OldGoldPurchaseRate.Read]
					},
					// {
					// 	'url': '/consumer-offer',
					// 	'title': 'Discount Offer',
					// 	'childSystemNameList': [UserPermission.ConsumerOffers.Read]
					// },
					{
						'url': '/promo-master',
						'title': 'Promo Master',
						'childSystemNameList': [UserPermission.PromoMaster.Read]
					},
					{
						'url': '/return-deduction',
						'title': 'Deduction Master',
						'childSystemNameList': [UserPermission.ReturnDeduction.Read]
					},
					{
						'url': '/repair-work/list',
						'title': 'Repair Work',
						'childSystemNameList': [UserPermission.RepairWork.Read]
					},
					{
						'url': '/birthday-anniversary-list',
						'title': 'Birthday/Anniversary List',
						'childSystemNameList': [
							UserPermission.BirthdayAnniversary.Read
						]
					},
					{
						'url': '/store-expenses',
						'title': 'Store Expenses',
						'childSystemNameList': [
							UserPermission.StoreExpence.Read,
							UserPermission.StoreExpence.Write
						]
					},
					{
						'url': '/faq-list/faq-add',
						'title': 'FAQ',
						'childSystemNameList': [
							UserPermission.FAQMaster.Read,
							UserPermission.FAQMaster.Write,
							UserPermission.FAQMaster.Delete
						]
					},
					{
						'url': '/customer-master/walk-in',
						'title': 'Customer History',
						'childSystemNameList': [
							UserPermission.CustomerWalkIn.Read,
							UserPermission.CustomerWalkIn.Write,
						]
					},
					{
						'url': '/eod-master',
						'requiresStoreSelection': true,
						'title': 'Eod Target',
						'childSystemNameList': [UserPermission.EodTarget.Read]
					},

					{
						'url': '/razorpay-config',
						'title': 'Razorpay',
						'childSystemNameList': [UserPermission.RazorpayConfig.Read]
					},
					// {
					// 	'url': '/pinelab-config',
					// 	'title': 'PineLab Config',
					// 	'childSystemNameList': [UserPermission.RazorpayConfig.Read]
					// },
					{
						'url': '/walk-in-source',
						'title': 'Walk In Source',
						'childSystemNameList': [UserPermission.WalkInSource.Read]
					},
					{
						'url': '/cash-entry',
						'title': 'Cash Entry',
						'childSystemNameList': [
							UserPermission.CashEntry.Read,
							UserPermission.CashEntry.Write,
							UserPermission.CashEntry.Delete,
						]
					},
					{
						'title': 'Send Notification',
						'isModal': true,
						'modalKey': 'send-notification',
						'childSystemNameList': [
							UserPermission.NotificationCenter.Write,
						]
					},

					{
						'url': '/gd-making-charge',
						'title': 'GD Making Charge',
						'childSystemNameList': [
							UserPermission.GDMakingCharge.Read
						]
					},
					{
						'url': '/support-user',
						'title': 'Support Team',
						'requiresStoreSelection': true,
						// 'childSystemNameList': [
						// 	UserPermission.GDMakingCharge.Read
						// ]
					},

					{
						'title': 'User Management',
						'caret': true,
						'systemNameList': [
							UserPermission.User.Read, UserPermission.User.Write, UserPermission.User.Delete,
							UserPermission.Roles.Read, UserPermission.Roles.Write, UserPermission.Roles.Delete,
						],
						'submenu': [
							{
								'url': '/user',
								'title': 'Users',
								'childSystemNameList': [
									UserPermission.User.Read,
									UserPermission.User.Write,
									UserPermission.User.Delete,
								]
							},
							{
								'url': '/roles',
								'requiresStoreSelection': true,
								'title': 'User Role',
								'childSystemNameList': [
									UserPermission.Roles.Read,
									UserPermission.Roles.Write,
									UserPermission.Roles.Delete,
								]
							}
						]
					},

					// {
					// 	'url': '/discount-voucher-list',
					// 	'title': 'Discount Voucher',
					// 	'childSystemNameList': [UserPermission.WalkInSource.Read]
					// },


				]
			},

			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.inword),
				'title': 'Inward',
				'caret': 'true',
				'systemNameList': [
					UserPermission.Inward.Read, UserPermission.Inward.Write,
				],
				'submenu': [
					{
						'url': '/inward-inventory/diamond-inward',
						'title': 'Diamond Inward',
						'badge': this.headerCounts.diamondInwardCount > 0 ? this.headerCounts.diamondInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write,]
					},
					{
						'url': '/inward-inventory/gold-inward',
						'title': 'Gold Inward',
						'badge': this.headerCounts.goldInwardCount > 0 ? this.headerCounts.goldInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write,]
					},
					{
						'url': '/inward-inventory/snmcc-diamond-inward',
						'title': 'SNMCC Diamond Inward',
						'badge': this.headerCounts.diamondSnmccInwardCount > 0 ? this.headerCounts.diamondSnmccInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write,]
					},
					{
						'url': '/inward-inventory/snmcc-gold-inward',
						'title': 'SNMCC Gold Inward',
						'badge': this.headerCounts.goldSnmccInwardCount > 0 ? this.headerCounts.goldSnmccInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write]
					},
					{
						'url': '/inward-inventory/repair-inward',
						'title': 'Repair Inward',
						'badge': this.headerCounts.repairInwardCount > 0 ? this.headerCounts.repairInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write,]
					},
					{
						'url': '/inward-inventory/consignment-diamond-inward',
						'title': 'Consignment Diamond Inward',
						'badge': this.headerCounts.diamondConsignmentInwardCount > 0 ? this.headerCounts.diamondConsignmentInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write,]
					},
					{
						'url': '/inward-inventory/consignment-gold-inward',
						'title': 'Consignment Gold Inward',
						'badge': this.headerCounts.goldConsignmentInwardCount > 0 ? this.headerCounts.goldConsignmentInwardCount : 0,
						'childSystemNameList': [UserPermission.Inward.Read, UserPermission.Inward.Write]
					},

				]
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.productInventory),
				'title': 'Inventory',
				'caret': 'true',
				'systemNameList': [
					UserPermission.Inventory.Read, UserPermission.Inventory.Write,
					UserPermission.ReturnToHK.Read, UserPermission.ReturnToHK.Write,
					UserPermission.ProductHistory.Read, UserPermission.ProductHistory.Write,
				],
				'submenu': [
					{
						'url': '/product-inventory',
						'title': 'Product Inventory',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write]
					},
					{
						'url': '/product-inventory/frozen-inventory',
						'title': 'Frozen Inventory',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write]
					},
					{
						'url': '/return-to-hk',
						'title': 'Return to HK',
						'childSystemNameList': [UserPermission.ReturnToHK.Read, UserPermission.ReturnToHK.Write]
					},
					{
						'url': '/product-history',
						'title': 'Product History',
						'childSystemNameList': [UserPermission.ProductHistory.Read, UserPermission.ProductHistory.Write]
					}
				]
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.returnToHk),
				'title': 'HO Return',
				'caret': true,
				'systemNameList': [
					UserPermission.ShipToLocationMaster.Read, UserPermission.ShipToLocationMaster.Write,
					UserPermission.Shipment.ViewOpenTransactions, UserPermission.Shipment.Create,
					UserPermission.Shipment.ViewAllInward, UserPermission.Shipment.Inward,
					UserPermission.Shipment.ViewReturnShipments, UserPermission.Shipment.AcceptReturn,
					UserPermission.Shipment.ViewRaisedShipments,
					UserPermission.Grn.ViewReturnToHO, UserPermission.Grn.ViewHORecall, UserPermission.Grn.ViewOGRequest,
					UserPermission.Grn.ViewSISRequest, UserPermission.Grn.ViewRepair, UserPermission.Grn.AcceptReject,
					UserPermission.HORecall.Read, UserPermission.HORecall.Write,
				],
				'submenu': [
					{
						'url': '/ship-to-location-mapping',
						'title': 'Ship-To Location Master',
						'childSystemNameList': [UserPermission.ShipToLocationMaster.Read, UserPermission.ShipToLocationMaster.Write]
					},
					{
						'url': '/shipment-to-ho/open-transactions',
						'title': 'Shipment To HO',
						'childSystemNameList': [UserPermission.Shipment.ViewOpenTransactions, UserPermission.Shipment.Create]
					},
					{
						'url': '/shipment-to-ho/inward',
						'title': 'Shipment Inward (All Stores)',
						'childSystemNameList': [UserPermission.Shipment.ViewAllInward, UserPermission.Shipment.Inward]
					},
					{
						'url': '/shipment-to-ho/return-shipments',
						'title': 'Return Shipments',
						'childSystemNameList': [UserPermission.Shipment.ViewReturnShipments, UserPermission.Shipment.AcceptReturn]
					},
					{
						'url': '/shipment-to-ho/grn',
						'title': 'GRN',
						'childSystemNameList': [
							UserPermission.Grn.ViewReturnToHO,
							UserPermission.Grn.ViewHORecall,
							UserPermission.Grn.ViewOGRequest,
							UserPermission.Grn.ViewSISRequest,
							UserPermission.Grn.ViewRepair,
							UserPermission.Grn.AcceptReject
						]
					},
					{
						'url': '/ho-recall',
						'title': 'HO Recall',
						'childSystemNameList': [UserPermission.HORecall.Read, UserPermission.HORecall.Write]
					},
				]
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.transaction),
				'title': 'Transaction',
				'caret': 'true',
				'systemNameList': [
					UserPermission.OldGoldPurchase.Read, UserPermission.OldGoldPurchase.Write,
					UserPermission.SalesEntry.Read, UserPermission.SalesEntry.Write,
					UserPermission.CustomerWalkIn.Read, UserPermission.CustomerWalkIn.Write,
					UserPermission.ReturnToHK.Read, UserPermission.ReturnToHK.Write,
					UserPermission.SalesInventory.Read, UserPermission.SalesInventory.Write,
					UserPermission.Repair.Read,
					UserPermission.Repair.Write,
					UserPermission.CustomerRefund.Read,
					UserPermission.EstimatePayment.Read,
					UserPermission.EstimatePayment.Write
				],
				'submenu': [
					{
						'url': '/transaction/new-sales-entry',
						'title': 'New Sales Entry',
						'childSystemNameList': [
							UserPermission.SalesEntry.Read,
							UserPermission.SalesEntry.Write
						]
					},
					{
						'url': '/transaction/sales-entry-list',
						'title': 'Sales Entry List',
						'childSystemNameList': [
							UserPermission.SalesEntry.Read,
							UserPermission.SalesEntry.Write
						]
					},
					{
						'url': '/transaction/draft-history',
						'title': 'Draft Sales Entry',
						'childSystemNameList': [
							UserPermission.SalesEntry.Read,
							UserPermission.SalesEntry.Write
						]
					},
					{
						'url': '/transaction/customer-refunds',
						'title': 'Customer Refunds',
						'childSystemNameList': [
							UserPermission.CustomerRefund.Read,
						]
					},


					{
						'title': 'Estimate Payment',
						'caret': 'true',
						'childSystemNameList': [
							UserPermission.EstimatePayment.Read,
							UserPermission.EstimatePayment.Write
						],
						'submenu': [
							{
								'url': '/estimate-payment/receipt-entry',
								'title': 'Estimate Payment Receipt',
								'childSystemNameList': [
									UserPermission.EstimatePayment.Read,
									UserPermission.EstimatePayment.Write
								]
							},
							{
								'url': '/estimate-payment/custom-data-list',
								'title': 'Custom Order',
								'childSystemNameList': [
									UserPermission.EstimatePayment.Read,
									UserPermission.EstimatePayment.Write
								]
							},
							{
								'url': '/estimate-payment/draft-history',
								'title': 'Estimate-Payment-Draft-History',
								'childSystemNameList': [
									UserPermission.EstimatePayment.Read,
									UserPermission.EstimatePayment.Write
								]
							}
						]
					},

					{
						'url': '/old-gold',
						'title': 'Old Gold',
						'childSystemNameList': [
							UserPermission.OldGoldPurchase.Read,
							UserPermission.OldGoldPurchase.Write
						]
					},

					{
						'url': '/sis/po-item-bagging',
						'title': 'Shop in Shop',
						'childSystemNameList': [
							UserPermission.SIS.Read,
							UserPermission.SIS.Write
						]
					},
					{
						'url': '/ecom/ecom-purchase-order-items',
						'title': 'Ecom Purchase Order Items',
						'childSystemNameList': [
							UserPermission.SIS.Read,
							UserPermission.SIS.Write
						]
					},

					{
						'title': 'Gold Rate Protection',
						'caret': 'true',
						'childSystemNameList': [
							UserPermission.GoldRateProtection.Read,
							UserPermission.GoldRateProtection.Write
						],
						'submenu': [
							{
								'url': '/gold-rate-protection/list',
								'title': 'Gold Rate Protection List',
								'childSystemNameList': [
									UserPermission.GoldRateProtection.Read,
									UserPermission.GoldRateProtection.Write
								]
							},
							// {
							// 	'url': '/gold-rate-protection/draft-history',
							// 	'title': 'Draft History',
							// 	'childSystemNameList': [
							// 		UserPermission.GoldRateProtection.Read,
							// 		UserPermission.GoldRateProtection.Write
							// 	]
							// }
						]
					},

					{
						'title': 'Repair',
						'caret': 'true',
						'childSystemNameList': [
							UserPermission.Repair.Read,
							UserPermission.Repair.Write
						],
						'submenu': [
							{
								'url': '/repair/search-product',
								'title': 'Search Product',
								'childSystemNameList': [
									UserPermission.Repair.Read,
									UserPermission.Repair.Write
								]
							},

							{
								'url': '/repair/repair-product-list',
								'title': 'Repair Product List',
								'childSystemNameList': [
									UserPermission.Repair.Read,
									UserPermission.Repair.Write
								]
							},
							{
								'url': '/repair/repair-list',
								'title': 'Repair Bag List',
								'childSystemNameList': [
									UserPermission.Repair.Read,
									UserPermission.Repair.Write
								]
							}
						]
					},

					{
						'title': 'Customer KYC Verification',
						'caret': 'true',
						'childSystemNameList': [
							UserPermission.KYCVerification.Read,
							UserPermission.KYCVerification.Write
						],
						'submenu': [
							{
								'url': '/kyc/match-cases',
								'title': 'Customer Match List',
								'childSystemNameList': [
									UserPermission.KYCVerification.Read,
									UserPermission.KYCVerification.Write
								]
							},
							{
								'url': '/kyc/not-match-cases',
								'title': 'Customer Not Match List',
								'childSystemNameList': [
									UserPermission.KYCVerification.Read,
									UserPermission.KYCVerification.Write
								]
							}
						]
					},

					// {
					// 	'title': 'Sales Inventory',
					// 	'url': '/repair/sales-inventory',
					// 	'childSystemNameList': [
					// 		UserPermission.SalesInventory.Read,
					// 		UserPermission.SalesInventory.Write
					// 	]
					// },
					// {
					// 	'url': '/product-estimate',
					// 	'title': 'Product Estimate',
					// 	'childSystemNameList': [UserPermission.SalesEntry.Read, UserPermission.SalesEntry.Write]

					// },
					// {
					// 	'url': 'product-estimate//product-estimate-list',
					// 	'title': 'Product Estimate List',
					// 	'childSystemNameList': [UserPermission.SalesEntry.Read, UserPermission.SalesEntry.Write]
					// }

				]
			},

			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.physicalVerification),
				'title': 'Inter Store',
				'caret': 'true',
				'systemNameList': [
					UserPermission.InterstoreTransfer.Read,
					UserPermission.Inventory.Read, UserPermission.Inventory.Write
				],
				'submenu': [
					{
						'url': '/interstore/product-request-list',
						'title': 'Product Request List',
						'childSystemNameList': [UserPermission.InterstoreTransfer.Read]
					},
					{
						'url': '/interstore/ladger',
						'title': 'My Ledger',
						'childSystemNameList': [UserPermission.InterstoreTransfer.Read]
					},
					{
						'url': '/inward-inventory/interstore-inward',
						'title': 'Interstore Inward',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write,]
					}

				]
			},

			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.returnToFranchise),
				'title': 'Return To Franchise',
				'caret': 'true',
				'systemNameList': [
					UserPermission.ReturnSalesOrders.Read,
				],
				'submenu': [
					{
						'url': '/return-order/return-to-franchise',
						'title': 'Return Customer To Franchise',
						'childSystemNameList': [UserPermission.ReturnSalesOrders.Read,]
					},
					{
						'url': '/return-order/return-sales-list',
						'title': 'Return Sales List',
						'childSystemNameList': [UserPermission.ReturnSalesOrders.Read,]
					},
					{
						'url': '/return-order/exchange-buy-back-product',
						'title': 'Exchange Buy Back Product List',
						'childSystemNameList': [UserPermission.ReturnSalesOrders.Read,]
					},
				]
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.snmcc),
				'title': 'SNMCC/Consignment',
				'caret': 'true',
				'systemNameList': [
					UserPermission.Inventory.Read, UserPermission.Inventory.Write,
				],
				'submenu': [
					{
						'url': '/snmcc/snmcc-return-product',
						'title': 'SNMCC Return Product',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write,]
					},
					{
						'url': '/snmcc/snmcc-selection',
						'title': 'SNMCC Selection',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write,]
					},

					{
						'url': '/consignment/consignment-return-product',
						'title': 'Consignment Return Product',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write,]
					},

					{
						'url': '/consignment/consignment-selection',
						'title': 'Consignment Selection',
						'childSystemNameList': [UserPermission.Inventory.Read, UserPermission.Inventory.Write,]
					}
				]
			},

			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.reports),
				'url': '/reports',
				'title': 'Reports',
				'systemNameList': [
					UserPermission.Reports.Read,
				],
			},

			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.FAQ),
				'title': 'Policies',
				'caret': 'true',
				'systemNameList': [
					UserPermission.FAQ.Read
				],
				'submenu': [
					{
						'url': '/faq-list',
						'title': 'FAQ',
						'childSystemNameList': [UserPermission.FAQ.Read]
					},
				]
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.transaction),
				'title': 'Enquiry',
				'caret': 'true',
				'systemNameList': [UserPermission.Enquiry.Read

				],
				'submenu': [
					{
						'url': '/enquiry',
						'title': 'Enquiry List',
						"childSystemNameList": [UserPermission.Enquiry.Read
						]
					},
				]
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.ecomShipmentOrders),
				'url': '/ecom-shipment-orders',
				'title': 'Ecom Shipment Orders',
				'systemNameList': [UserPermission.EcomShipmentOrder.Read],
			},
			{
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.digitalGold),
				'title': 'Digital Gold',
				'caret': 'true',
				'systemNameList': [UserPermission.DigitalGold.Read

				],
				'submenu': [
					{
						'url': '/digital-gold-transaction',
						'title': 'Digital Gold Transaction',
						"childSystemNameList": [UserPermission.DigitalGold.Read
						]
					},
				]
			},

		];

		// KMR (KisnaMeriRoshani) menu - gated purely by the caller's KMR role, resolved from KMR's
		// own database via KisnaMeriRoshaniAPI (see KmrAccessService), not a POS permission record -
		// KMR db remains the single source of truth for who can see/use these screens.
		if (this.kmrAccess.isFranchise || this.kmrAccess.isAdmin) {
			const kmrSubmenu: any[] = [];
			if (this.kmrAccess.isFranchise) {
				kmrSubmenu.push({
					'url': '/kmr/customer-plans',
					'title': 'Customer Plans & Collection',
				});
				kmrSubmenu.push({
					'url': '/kmr/create-plan',
					'title': 'Create KMR Plan',
				});
			}
			if (this.kmrAccess.isAdmin) {
				kmrSubmenu.push({
					'url': '/kmr/plan-schemes',
					'title': 'Plan Schemes & Settings',
				});
				kmrSubmenu.push({
					'url': '/kmr/admin-customer-plans',
					'title': 'Customer Plans (Admin)',
				});
				kmrSubmenu.push({
					'url': '/kmr/matured-plans-report',
					'title': 'Matured Plans Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/franchise-wise-enrollment-report',
					'title': 'Franchise Wise Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/all-user-report',
					'title': 'All User Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/franchise-wise-summary-report',
					'title': 'Franchise Wise Summary Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/day-to-day-collection-report',
					'title': 'Franchise Wise Day-to-Day Collection Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/franchise-ledger-report',
					'title': 'Franchise Ledger',
				});
				kmrSubmenu.push({
					'url': '/kmr/user-summary-report',
					'title': 'User Summary',
				});
				kmrSubmenu.push({
					'url': '/kmr/monthly-payment-report',
					'title': 'Monthly Payment Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/abandoned-payment-report',
					'title': 'Abandoned Payment Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/redemption-report',
					'title': 'Redemption Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/installment-collection-report',
					'title': 'KMR Installment Collection',
				});
				kmrSubmenu.push({
					'url': '/kmr/deleted-user-payment-report',
					'title': 'Deleted UserPayment Report',
				});
				kmrSubmenu.push({
					'url': '/kmr/user-list',
					'title': 'User List',
				});
				kmrSubmenu.push({
					'url': '/kmr/franchise-list',
					'title': 'Franchise List',
				});
			}

			// No systemNameList/childSystemNameList here - KMR visibility is governed purely by
			// kmrAccess above (KmrAccessService, KMR's own DB), not POS permission records. Gating
			// this on POS permissions too silently hid the whole menu for any KMR-authorized user
			// whose POS role wasn't separately granted the matching Kmr.*.Read permission strings.
			menus.push({
				'svg': this.sanitizer.bypassSecurityTrustHtml(AppIcons.digitalGold),
				'title': 'Meri Roshni',
				'caret': 'true',
				'submenu': kmrSubmenu,
			} as any);
		}

		return menus;
	}

}
